import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";

export const MessageTemplates = () => {
    const [items, setItems] = useState(null);
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await apiClient.get("/admin/message-templates");
            setItems(data.items || []);
        } catch (e) {
            console.error("Message templates load failed:", e);
            setItems([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const update = (id, field, value) =>
        setItems(items.map((t) => (t.id === id ? { ...t, [field]: value } : t)));

    const save = async (tpl) => {
        if (!tpl.name.trim() || !tpl.subject.trim() || !tpl.body.trim()) {
            toast.error("Name, Betreff und Text dürfen nicht leer sein.");
            return;
        }
        setSavingId(tpl.id);
        try {
            await apiClient.put(`/admin/message-templates/${tpl.id}`, {
                name: tpl.name, subject: tpl.subject, body: tpl.body,
            });
            toast.success("Vorlage gespeichert.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    const add = async () => {
        try {
            const { data } = await apiClient.post("/admin/message-templates", {
                name: "Neue Vorlage",
                subject: "Deine Bewerbung bei Möbus Umweltschutz",
                body: "Hallo {vorname},\n\n\n\nBeste Grüße\ndein Team der Möbus Umweltschutz GmbH",
            });
            setItems([...items, data]);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Diese Vorlage wirklich löschen?")) return;
        try {
            await apiClient.delete(`/admin/message-templates/${id}`);
            setItems(items.filter((t) => t.id !== id));
            toast.success("Vorlage gelöscht.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    if (items === null) {
        return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div>;
    }

    return (
        <div data-testid="message-templates" className="mt-10">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-bold text-moebus-deep">Individuelle E-Mail-Vorlagen</h3>
                <button
                    data-testid="add-message-template"
                    onClick={add}
                    className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md"
                >
                    <Plus size={14} /> Neue Vorlage
                </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
                Diese Vorlagen stehen beim Versand individueller E-Mails aus der Bewerber-Detailansicht zur Auswahl.
                Platzhalter: {"{vorname}"}, {"{nachname}"}, {"{position}"}.
            </p>
            <div className="grid gap-4">
                {items.map((tpl) => (
                    <div key={tpl.id} data-testid={`message-template-${tpl.id}`} className="bg-white border border-moebus-border rounded-xl p-4 grid gap-2.5">
                        <div className="flex gap-2">
                            <input
                                data-testid="mt-name-input"
                                value={tpl.name}
                                onChange={(e) => update(tpl.id, "name", e.target.value)}
                                placeholder="Name der Vorlage"
                                className="flex-1 text-sm font-medium border border-moebus-border rounded-md px-3 py-2"
                            />
                            <button
                                data-testid="mt-delete-btn"
                                onClick={() => remove(tpl.id)}
                                className="p-2 rounded-md text-red-500 hover:bg-red-50"
                                title="Löschen"
                            >
                                <Trash2 size={15} />
                            </button>
                        </div>
                        <input
                            data-testid="mt-subject-input"
                            value={tpl.subject}
                            onChange={(e) => update(tpl.id, "subject", e.target.value)}
                            placeholder="Betreff"
                            className="text-sm border border-moebus-border rounded-md px-3 py-2"
                        />
                        <textarea
                            data-testid="mt-body-input"
                            value={tpl.body}
                            onChange={(e) => update(tpl.id, "body", e.target.value)}
                            rows={5}
                            className="text-sm border border-moebus-border rounded-md px-3 py-2"
                        />
                        <button
                            data-testid="mt-save-btn"
                            onClick={() => save(tpl)}
                            disabled={savingId === tpl.id}
                            className="inline-flex items-center gap-2 self-start bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-md text-sm disabled:opacity-60"
                        >
                            <Save size={14} /> {savingId === tpl.id ? "Speichern…" : "Speichern"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};
